import { useEffect, useRef, useState } from 'react';
import iframeResize from 'iframe-resizer/js/iframeResizer.js';
import { lambda } from 'src/_shared';
import { usePermission } from 'src/_shared/components/permission-gate/usePermission.js';
import { PhoneExpandable } from 'src/_shared/components/phone-expandable';
import { isPhone } from 'src/_shared/phone.js';

const RESIZER_MSG = '[iFrameSizer]';
// Narrowest the frame can be laid out at and keep its five tabs on one line.
const MIN_INNER_WIDTH = 393;
// Smallest height the frame is probed at; no dashboard tab is this short.
const PROBE_MIN = 120;
// Metabase fires resets ~every 16ms while a tab renders; probe once it's quiet.
const SETTLE_MS = 200;
// Longest we wait for the child's answer to a probe (it normally takes ms).
const REPLY_MAX_MS = 500;
// Title, tabs and the first few cards show before Show more.
const COLLAPSED_HEIGHT = 900;
// Metabase ends every tab with a separator and empty footer space, in frame
// pixels; the wrapper stops short of it so the pill follows the last card.
const FOOTER_CROP = 58;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// On a phone the embed sits in its own card, so Metabase's border goes.
const withoutBorder = (url) => {
	if (!url || !isPhone()) return url;
	const [base, hash = ''] = url.split('#');
	const params = new URLSearchParams(hash);
	params.set('bordered', 'false');
	return `${base}#${params.toString()}`;
};
const nextPaint = () =>
	new Promise((resolve) =>
		requestAnimationFrame(() => requestAnimationFrame(resolve))
	);

function MetabaseDashboardEmbed({ companyId }) {
	const [iFrameUrl, setFrameUrl] = useState();
	const frameRef = useRef(null);
	const wrapRef = useRef(null);
	const showMetabaseDashboard = usePermission('ui.dashboard.metabase', true);

	useEffect(() => {
		const getReportUrl = async () => {
			const data = await lambda({
				endpoint: 'metabaseEmbeds',
				variables: {
					companyId: companyId,
					reportType: 'dashboard',
					reportId: 13,
				},
			});
			setFrameUrl(data.iframeUrl);
		};
		getReportUrl();
	}, [companyId]);

	// On a phone the frame grows to fit whichever dashboard tab is open, so
	// the page is the only thing that scrolls (ERIN-8561). Metabase embeds
	// ship the iframe-resizer child script: once the parent side answers it,
	// Metabase drops its own scroll box and reports its height on every
	// change. Growth comes through on its own; shrinking does not, because
	// Metabase's body fills the frame, so every measure is at least the
	// frame's own height and a shorter tab only produces "reset" messages.
	// After a burst of those we probe: shrink the frame to the bottom of its
	// visible part (nothing on screen moves), ask the child to re-measure,
	// and hold the page length until the real height comes back.
	//
	// The frame also keeps the column the cards use: it is laid out at least
	// MIN_INNER_WIDTH wide (the five tabs' minimum) and scaled down to the
	// column, with the wrapper sized to the scaled result. Desktop keeps the
	// fixed frame.
	useEffect(() => {
		const frame = frameRef.current;
		const wrap = wrapRef.current;
		if (!frame || !wrap || !iFrameUrl || !isPhone()) return undefined;

		let scale = 1;
		let columnWidth = 0;

		const fit = () => {
			const height = Math.max(frame.offsetHeight - FOOTER_CROP, 0);
			wrap.style.height = `${Math.round(height * scale)}px`;
		};

		const layout = () => {
			const width = wrap.clientWidth;
			if (!width || width === columnWidth) return;
			columnWidth = width;
			const inner = Math.max(MIN_INNER_WIDTH, width);
			scale = width / inner;
			frame.style.width = `${inner}px`;
			frame.style.transform = scale < 1 ? `scale(${scale})` : '';
			fit();
		};

		layout();
		const wrapObserver = new ResizeObserver(layout);
		wrapObserver.observe(wrap);
		const frameObserver = new ResizeObserver(fit);
		frameObserver.observe(frame);

		iframeResize({ scrolling: false }, frame);

		let timer;
		let probing = false;
		let sized = false; // a real size has come in since init
		let onReply = null;
		let enforced = null; // the last measured height, re-applied over late resets

		// Shrinks the frame, lets the child's viewport take the new size (a
		// transformed frame asked at once answered with the old size), asks
		// for a measure and waits for the direct answer, or REPLY_MAX_MS.
		// While the frame is small Metabase re-renders and its own resets
		// carry the small viewport height, which the library applies, so
		// only the answer counts and it is asserted over them. A stale
		// answer puts the old height back, so that gets one retry.
		const probe = async (height) => {
			const previous = frame.offsetHeight;
			for (let attempt = 0; attempt < 2; attempt += 1) {
				enforced = null;
				frame.style.height = `${height}px`;
				await nextPaint();
				let answer = null;
				const replied = new Promise((resolve) => {
					onReply = (measured) => {
						answer = measured;
						resolve();
					};
				});
				frame.iFrameResizer?.resize();
				await Promise.race([replied, wait(REPLY_MAX_MS)]);
				onReply = null;
				if (answer !== null) {
					enforced = answer;
					frame.style.height = `${answer}px`;
				}
				await nextPaint();
				if (frame.offsetHeight !== previous || height >= previous) break;
			}
			return frame.offsetHeight;
		};

		const recheck = async () => {
			const frameHeight = frame.offsetHeight;
			const top = frame.getBoundingClientRect().top;
			const visibleBottom = Math.ceil((window.innerHeight - top) / scale);
			const first = Math.min(Math.max(visibleBottom, PROBE_MIN), frameHeight);
			if (first >= frameHeight) return;

			probing = true;
			wrap.style.minHeight = wrap.style.height;
			let reported = await probe(first);
			// Content ended above the visible bottom: the child could only
			// answer with the probe height, so measure again from the top.
			if (Math.abs(reported - first) <= 1 && first > PROBE_MIN) {
				reported = await probe(PROBE_MIN);
			}
			wrap.style.minHeight = '';
			fit();
			await wait(SETTLE_MS);
			probing = false;
			enforced = null;
		};

		const onMessage = (event) => {
			if (
				event.source !== frame.contentWindow ||
				typeof event.data !== 'string' ||
				!event.data.startsWith(RESIZER_MSG)
			) {
				return;
			}
			const parts = event.data.slice(RESIZER_MSG.length).split(':');
			const type = parts[3];
			const height = Number(parts[1]);
			if (type === 'resizeParent' && onReply) {
				onReply(height);
				return;
			}
			if (type !== 'reset' && type !== 'resetPage') {
				if (type !== 'init') sized = true;
				return;
			}
			if (enforced !== null && height !== enforced) {
				frame.style.height = `${enforced}px`;
				return;
			}
			if (type !== 'reset' || probing || !sized) return;
			clearTimeout(timer);
			timer = setTimeout(recheck, SETTLE_MS);
		};

		window.addEventListener('message', onMessage);
		return () => {
			clearTimeout(timer);
			wrapObserver.disconnect();
			frameObserver.disconnect();
			window.removeEventListener('message', onMessage);
			frame.iFrameResizer?.removeListeners();
		};
	}, [iFrameUrl]);

	return showMetabaseDashboard ? (
		<div className="metabase-dashboard-card">
			<PhoneExpandable collapsedHeight={COLLAPSED_HEIGHT}>
				<div ref={wrapRef} className="metabase-dashboard-embed">
					<iframe
						ref={frameRef}
						className="metabase-dashboard-frame"
						src={withoutBorder(iFrameUrl)}
						frameBorder="0"
						width={'100%'}
						height={'2500'}
						title="Dashboard"
					/>
				</div>
			</PhoneExpandable>
		</div>
	) : null;
}

export default MetabaseDashboardEmbed;
